// The service desk: your IT and ERP team's queue and the provider's, with SLA clocks, and the ticket page where
// tickets are worked, handed to the outsourced IT provider and resolved. Provider staff see the same screens,
// limited to their queue.
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CommandBar } from "../Shell";
import { Icon } from "../icons";
import { ErrorBox, ERP_ENTITIES } from "../ui";
import { useLoad } from "../hooks";
import { sd, CLOCK_LABEL, STATE_LABEL, due, when, type Clock, type SdMe, type Ticket, type TicketDetail } from "./sdApi";

export function ClockChip({ c, at, label }: { c: Clock; at: string | null; label?: string }) {
  if (c === "none") return <span className="muted">–</span>;
  const text = c === "met" || c === "missed" || c === "paused" ? CLOCK_LABEL[c] : due(at);
  return <span className={`sd-clock c-${c}`} title={at ? `${label ? `${label} ` : ""}due ${when(at)}` : undefined}>{text}</span>;
}
export const Prio = ({ p }: { p: string }) => <span className={`sd-prio ${p}`}>{p}</span>;
export const StateChip = ({ s }: { s: string }) => <span className={`sd-state s-${s}`}>{STATE_LABEL[s] ?? s}</span>;

/** The clock that matters now: the provider's while it's with them, else response until answered, then resolve. */
function nextClock(t: Ticket): { c: Clock; at: string | null; label: string } {
  if (t.state === "with_provider" && t.provider) return { c: t.provider.clock, at: t.provider.due, label: "Provider resolve" };
  if (!t.respondedAt && !["resolved", "closed", "cancelled"].includes(t.state)) return { c: t.response, at: t.responseDue, label: "Response" };
  return { c: t.resolve, at: t.resolveDue, label: "Resolve" };
}

export function DeskPage() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const me = useLoad(() => sd.me());
  const role = me.data?.role;
  const viewKey = sp.get("view") ?? "open";
  const [q, setQ] = useState("");
  const [tick, setTick] = useState(0);
  const counts = useLoad(() => (role === "agent" ? sd.counts() : Promise.resolve({} as Record<string, number>)), [role, tick]);
  const list = useLoad(() => (role ? sd.tickets(role === "provider" && viewKey === "open" ? "open" : viewKey, q.trim()) : Promise.resolve(null as any)), [role, viewKey, q, tick]);
  const [bcNote, setBcNote] = useState<string | null>(null);
  // Failed BC job queue entries become tickets when the desk opens (the server checks at most every 10 minutes).
  useEffect(() => {
    if (role !== "agent") return;
    sd.checkBc().then((r) => { if (r.opened.length) { setBcNote(`${r.opened.length} failed BC job${r.opened.length > 1 ? "s" : ""} opened as tickets.`); setTick((x) => x + 1); } })
      .catch(() => { /* not published or BC not connected: the button explains */ });
  }, [role]);
  useEffect(() => { if (role === "requester") nav("/help", { replace: true }); }, [role]); // eslint-disable-line react-hooks/exhaustive-deps

  const views: { key: string; name: string; n?: number }[] = role === "agent" ? [
    { key: "open", name: "All open", n: counts.data?.open }, { key: "mine", name: "Mine", n: counts.data?.mine }, { key: "unassigned", name: "Unassigned", n: counts.data?.unassigned },
    { key: "breaching", name: "Overdue", n: counts.data?.breaching }, { key: "provider", name: "With provider", n: counts.data?.provider }, { key: "approval", name: "Awaiting approval", n: counts.data?.approval },
    ...(me.data?.queues ?? []).map((x) => ({ key: `queue:${x.id}`, name: x.name })), { key: "resolved", name: "Resolved" },
  ] : [{ key: "open", name: "Open" }, { key: "resolved", name: "Resolved" }];
  const setView = (k: string) => setSp(k === "open" ? {} : { view: k });

  const check = async () => {
    setBcNote("Checking Business Central…");
    try { const r = await sd.checkBc(true); setBcNote(r.opened.length ? `Opened ${r.opened.join(", ")} for failed job queue entries.` : `No new job queue failures (${r.failed ?? 0} in error, all already ticketed).`); setTick((x) => x + 1); }
    catch (e) { setBcNote(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <>
      <CommandBar status={list.data ? `${list.data.length} ticket${list.data.length === 1 ? "" : "s"}` : "Loading…"}>
        {role === "agent" && <Link className="cmd primary" to="/service-desk/new"><Icon name="plus" size={14} />New ticket</Link>}
        <button className="cmd" onClick={() => setTick((x) => x + 1)}>Refresh</button>
        {role === "agent" && <button className="cmd" onClick={check}>Check BC job queue</button>}
        {role === "agent" && <Link className="cmd" to="/service-desk/report">Report</Link>}
        {role === "agent" && <Link className="cmd" to="/service-desk/settings">Settings</Link>}
        {role === "agent" && <Link className="cmd" to="/help">Get help</Link>}
      </CommandBar>
      <div className="page">
        <div className="page-title"><h1>{role === "provider" ? `${me.data?.queues.map((x) => x.name).join(", ")}` : "Service desk"}</h1>
          <span className="sub">{role === "provider" ? "Tickets the company has handed to you" : "Your team and your IT provider, one queue"}</span></div>
        {role === "agent" && me.data && !me.data.membersSet && <div className="msg warn">Everyone can work the desk until you list your team. <Link to="/service-desk/settings">Add your agents and your IT provider</Link>.</div>}
        {bcNote && <div className="msg info">{bcNote}</div>}
        <ErrorBox error={me.error ?? list.error} />
        <div className="sd-desk">
          <nav className="sd-views" aria-label="Views">
            {views.map((v) => <button key={v.key} aria-current={viewKey === v.key} onClick={() => setView(v.key)}><span>{v.name}</span>{v.n !== undefined && <span className={`n ${v.key === "breaching" && v.n ? "hot" : ""}`}>{v.n}</span>}</button>)}
          </nav>
          <section className="card sd-list">
            <div className="filters" style={{ padding: 10, margin: 0, borderBottom: "1px solid var(--stroke)" }}>
              <input className="input" type="search" aria-label="Filter tickets" placeholder="Filter by number, title or requester" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 360 }} />
            </div>
            {!list.data ? <div className="empty">Loading…</div> : !list.data.length ? <div className="empty">Nothing here.</div> : (
              <div className="table-wrap"><table className="dgrid">
                <thead><tr><th>Number</th><th>Title</th><th>Pri</th><th>Status</th><th>Next due</th><th>Requester</th><th>Queue</th><th>Assigned</th></tr></thead>
                <tbody>{(list.data as Ticket[]).map((t) => { const nc = nextClock(t); return (
                  <tr key={t.id}>
                    <td><Link className="num" to={`/service-desk/${t.number}`}>{t.number}</Link></td>
                    <td className="title" title={t.title}>{t.title}{t.source === "auto" && <span className="sd-tag">BC</span>}</td>
                    <td><Prio p={t.priority} /></td>
                    <td><StateChip s={t.state} /></td>
                    <td><ClockChip c={nc.c} at={nc.at} label={nc.label} /></td>
                    <td>{t.requesterName}</td>
                    <td className="muted">{t.queueName}{t.provider?.ref ? ` · ${t.provider.ref}` : ""}</td>
                    <td className="muted">{t.ownerName ?? "–"}</td>
                  </tr>); })}
                </tbody>
              </table></div>
            )}
          </section>
        </div>
      </div>
    </>
  );
}

export function NewTicketPage() {
  const nav = useNavigate();
  const me = useLoad(() => sd.me());
  const [b, setB] = useState({ title: "", description: "", name: "", email: "", queueId: "", kind: "incident", impact: 3, urgency: 3 });
  const [err, setErr] = useState<unknown>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr(null);
    try {
      const t = await sd.create({ title: b.title, description: b.description, kind: b.kind, impact: b.impact, urgency: b.urgency, queueId: b.queueId || undefined, requester: b.email || b.name ? { name: b.name || b.email, email: b.email || undefined } : undefined });
      nav(`/service-desk/${t.number}`);
    } catch (x) { setErr(x); }
  };
  return (
    <>
      <CommandBar><Link className="cmd" to="/service-desk">Service desk</Link></CommandBar>
      <div className="page sd-form">
        <div className="page-title"><h1>New ticket</h1><span className="sub">For a call, a walk-up or an email. Leave the requester blank if it's yours.</span></div>
        <form className="card" style={{ padding: 20 }} onSubmit={submit}>
          <div className="field"><label htmlFor="nt-title">Title</label><input id="nt-title" className="input" required value={b.title} onChange={(e) => setB({ ...b, title: e.target.value })} /></div>
          <div className="sd-two">
            <div className="field"><label htmlFor="nt-name">Requester name</label><input id="nt-name" className="input" value={b.name} onChange={(e) => setB({ ...b, name: e.target.value })} /></div>
            <div className="field"><label htmlFor="nt-email">Requester email</label><input id="nt-email" className="input" type="email" value={b.email} onChange={(e) => setB({ ...b, email: e.target.value })} /></div>
          </div>
          <div className="field"><label htmlFor="nt-desc">Details</label><textarea id="nt-desc" className="textarea" rows={5} value={b.description} onChange={(e) => setB({ ...b, description: e.target.value })} /></div>
          <div className="sd-four">
            <div className="field"><label htmlFor="nt-kind">Type</label><select id="nt-kind" className="select" value={b.kind} onChange={(e) => setB({ ...b, kind: e.target.value })}><option value="incident">Incident</option><option value="request">Request</option><option value="problem">Problem</option><option value="change">Change</option></select></div>
            <div className="field"><label htmlFor="nt-q">Queue</label><select id="nt-q" className="select" value={b.queueId} onChange={(e) => setB({ ...b, queueId: e.target.value })}><option value="">Default</option>{me.data?.queues.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></div>
            <div className="field"><label htmlFor="nt-i">Impact</label><select id="nt-i" className="select" value={b.impact} onChange={(e) => setB({ ...b, impact: Number(e.target.value) })}><option value={1}>Company</option><option value={2}>Department</option><option value={3}>One person</option></select></div>
            <div className="field"><label htmlFor="nt-u">Urgency</label><select id="nt-u" className="select" value={b.urgency} onChange={(e) => setB({ ...b, urgency: Number(e.target.value) })}><option value={1}>Work stopped</option><option value={2}>Slowed down</option><option value={3}>Can wait</option></select></div>
          </div>
          <ErrorBox error={err} />
          <div className="btn-row"><button className="btn primary">Create ticket</button><Link className="btn" to="/service-desk">Cancel</Link></div>
        </form>
      </div>
    </>
  );
}

type Composer = "public" | "provider" | "internal";

export function TicketPage() {
  const { number = "" } = useParams();
  const [tick, setTick] = useState(0);
  const me = useLoad(() => sd.me());
  const t = useLoad(() => sd.ticket(number), [number, tick]);
  const [err, setErr] = useState<unknown>(null);
  const [note, setNote] = useState<string | null>(null);
  const refresh = () => setTick((x) => x + 1);
  const act = async (fn: () => Promise<unknown>, done?: string) => {
    setErr(null); setNote(null);
    try { const r: any = await fn(); if (done) setNote(done); if (r?.emailError) setNote(`${done ?? "Saved"}. Email not sent: ${r.emailError}`); refresh(); return true; } catch (x) { setErr(x); return false; }
  };
  const d = t.data;
  if (t.error) return <div className="page"><ErrorBox error={t.error} /><Link to="/service-desk">Back to the service desk</Link></div>;
  if (!d || !me.data) return <div className="page"><div className="empty">Loading…</div></div>;
  const agent = d.role === "agent", provider = d.role === "provider";
  const open = !["resolved", "closed", "cancelled"].includes(d.state);
  return (
    <>
      <CommandBar status={<>{d.number} · <StateChip s={d.state} /></>}>
        <Link className="cmd" to="/service-desk">Service desk</Link>
        {agent && open && !d.ownerId && <button className="cmd primary" onClick={() => act(() => sd.update(d.number, { ownerId: me.data!.id }), "Assigned to you")}>Assign to me</button>}
        {agent && open && d.ownerId === me.data.id && <button className="cmd" onClick={() => act(() => sd.update(d.number, { ownerId: null }))}>Unassign</button>}
        {!open && d.state === "resolved" && <button className="cmd" onClick={() => act(() => sd.reopen(d.number), "Reopened")}>Reopen</button>}
        {agent && d.state === "resolved" && <button className="cmd" onClick={() => act(() => sd.update(d.number, { state: "closed" }), "Closed")}>Close</button>}
        {agent && open && <button className="cmd" onClick={() => act(() => sd.update(d.number, { state: "cancelled", reason: "cancelled" }), "Cancelled")}>Cancel ticket</button>}
        {agent && <Link className="cmd" to={`/help/${d.number}`}>Requester's view</Link>}
      </CommandBar>
      <div className="page">
        <div className="page-title"><h1>{d.title}</h1><span className="sub">{d.number} · {d.kind}{d.requestType ? ` · ${d.requestType}` : ""} · raised by {d.requesterName} {when(d.createdAt)}</span></div>
        <ErrorBox error={err} />
        {note && <div className="msg info">{note}</div>}
        <div className="split">
          <div className="grow">
            <section className="card" style={{ padding: 16 }}>
              {d.description ? <p style={{ whiteSpace: "pre-wrap", marginTop: 0 }}>{d.description}</p> : <p className="muted" style={{ marginTop: 0 }}>No details given.</p>}
              {Object.keys(d.answers).length > 0 && <dl className="dl">{Object.entries(d.answers).flatMap(([k, v]) => [<dt key={`k${k}`}>{k}</dt>, <dd key={`v${k}`}>{v}</dd>])}</dl>}
            </section>
            <section className="sd-thread" aria-label="Conversation">
              {d.comments.map((c) => (
                <div key={c.id} className={`sd-msg v-${c.visibility} ${c.role === "system" ? "sys" : ""}`}>
                  <div className="h"><b>{c.authorName}</b><span className="muted"> · {c.role === "system" ? "update" : c.role}</span>
                    <span className={`sd-vis v-${c.visibility}`}>{c.visibility === "public" ? "Requester sees" : c.visibility === "provider" ? "Provider sees" : "Internal"}</span>
                    <span className="muted"> · {when(c.at)}</span>{c.emailedTo?.length ? <span className="muted"> · emailed {c.emailedTo.join(", ")}</span> : null}</div>
                  <div className="b">{c.body}</div>
                </div>
              ))}
              {!d.comments.length && <p className="muted">No conversation yet.</p>}
            </section>
            <ComposerBox d={d} onDone={refresh} setErr={setErr} setNote={setNote} />
          </div>
          <aside className="side">
            <section className="card" style={{ padding: 14 }}>
              <dl className="dl sd-dl">
                <dt>Status</dt><dd><StateChip s={d.state} /></dd>
                <dt>Priority</dt><dd>{agent ? (
                  <span className="btn-row">
                    <select className="select cell" aria-label="Impact" value={d.impact} onChange={(e) => act(() => sd.update(d.number, { impact: Number(e.target.value) }))}><option value={1}>Company</option><option value={2}>Department</option><option value={3}>One person</option></select>
                    <select className="select cell" aria-label="Urgency" value={d.urgency} onChange={(e) => act(() => sd.update(d.number, { urgency: Number(e.target.value) }))}><option value={1}>Work stopped</option><option value={2}>Slowed down</option><option value={3}>Can wait</option></select>
                    <Prio p={d.priority} />
                  </span>) : <Prio p={d.priority} />}</dd>
                <dt>Response</dt><dd><ClockChip c={d.response} at={d.responseDue} label="Response" /></dd>
                <dt>Resolve</dt><dd><ClockChip c={d.resolve} at={d.resolveDue} label="Resolve" /></dd>
                <dt>Queue</dt><dd>{agent && open ? <select className="select cell" aria-label="Queue" value={d.queueId} onChange={(e) => act(() => sd.update(d.number, { queueId: e.target.value }))}>{me.data.queues.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select> : d.queueName}</dd>
                <dt>Assigned</dt><dd>{d.ownerName ?? "Nobody"}</dd>
                <dt>Requester</dt><dd>{d.requesterName}{d.requesterEmail ? <div className="muted">{d.requesterEmail}</div> : null}</dd>
                <dt>Category</dt><dd>{d.category ?? "–"}</dd>
              </dl>
            </section>
            <ProviderBox d={d} me={me.data} act={act} />
            {(agent || d.links.length > 0) && <LinksBox d={d} agent={agent} act={act} />}
            {d.approvals.length > 0 && (
              <section className="card" style={{ padding: 14 }}>
                <h3 className="sd-h3">Approval</h3>
                {d.approvals.map((a) => <div key={a.id}>{a.approverEmail}: <b>{a.decision ?? "waiting"}</b>{a.note ? <div className="muted">{a.note}</div> : null}</div>)}
              </section>
            )}
            <section className="card" style={{ padding: 14 }}>
              <h3 className="sd-h3">History</h3>
              <ol className="sd-hist">{d.history.map((h, i) => <li key={i}><span>{STATE_LABEL[h.to] ?? h.to}</span><span className="muted">{h.reason ? `${h.reason} · ` : ""}{h.by} · {when(h.at)}</span></li>)}</ol>
            </section>
            {provider && <p className="muted" style={{ fontSize: 12 }}>You see the requester's conversation and notes for the provider, not the company's internal notes.</p>}
          </aside>
        </div>
      </div>
    </>
  );
}

function ComposerBox({ d, onDone, setErr, setNote }: { d: TicketDetail; onDone: () => void; setErr: (e: unknown) => void; setNote: (s: string | null) => void }) {
  const agent = d.role === "agent", provider = d.role === "provider";
  const [mode, setMode] = useState<Composer>(provider ? "provider" : "public");
  const [body, setBody] = useState("");
  const [wait, setWait] = useState(false);
  const [busy, setBusy] = useState(false);
  const open = !["resolved", "closed", "cancelled"].includes(d.state);
  const send = async (resolve = false) => {
    if (!body.trim()) return;
    setBusy(true); setErr(null); setNote(null);
    try {
      if (resolve) { await sd.resolve(d.number, body); setNote("Resolved. The requester has been told."); }
      else {
        const r = await sd.comment(d.number, { body, visibility: mode, waitForRequester: wait && mode === "public" });
        setNote(r.emailedTo.length ? `Sent, and emailed to ${r.emailedTo.join(", ")}.` : r.emailError ? `Saved. Email not sent: ${r.emailError}` : "Saved.");
      }
      setBody(""); setWait(false); onDone();
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };
  if (d.state === "closed" || d.state === "cancelled") return null;
  const modes: { k: Composer; l: string }[] = [{ k: "public", l: "Reply to requester" }, { k: "provider", l: agent ? "Note to provider" : "Note to the company" }, ...(agent ? [{ k: "internal" as Composer, l: "Internal note" }] : [])];
  return (
    <section className={`card sd-compose m-${mode}`}>
      <div className="pivot" role="tablist" style={{ margin: 0, padding: "0 8px" }}>
        {modes.map((m) => <button key={m.k} role="tab" aria-selected={mode === m.k} onClick={() => setMode(m.k)}>{m.l}</button>)}
      </div>
      <div style={{ padding: 12 }}>
        <textarea className="textarea" rows={4} aria-label={modes.find((m) => m.k === mode)?.l} value={body} onChange={(e) => setBody(e.target.value)}
          placeholder={mode === "public" ? `${d.requesterName} sees this${d.requesterEmail ? " and gets it by email" : ""}.` : mode === "provider" ? (agent ? "The provider sees this, not the requester." : "The company's service desk sees this, not the requester.") : "Only your team sees this."} />
        <div className="btn-row" style={{ marginTop: 8 }}>
          <button className="btn primary" disabled={busy || !body.trim()} onClick={() => send(false)}>{mode === "public" ? "Send reply" : "Add note"}</button>
          {mode === "public" && agent && open && <label className="sd-check"><input type="checkbox" checked={wait} onChange={(e) => setWait(e.target.checked)} />Wait for their answer (pauses the clock)</label>}
          {mode === "public" && open && (agent || provider) && <button className="btn" disabled={busy || !body.trim()} onClick={() => send(true)}>Send and resolve</button>}
        </div>
      </div>
    </section>
  );
}

function ProviderBox({ d, me, act }: { d: TicketDetail; me: SdMe; act: (fn: () => Promise<unknown>, done?: string) => Promise<boolean> }) {
  const agent = d.role === "agent", provider = d.role === "provider";
  const [note, setNote] = useState("");
  const [ref, setRef] = useState(d.provider?.ref ?? "");
  useEffect(() => setRef(d.provider?.ref ?? ""), [d.provider?.ref]);
  const providers = me.queues.filter((x) => x.kind === "provider");
  const [qid, setQid] = useState(providers[0]?.id ?? "");
  const open = !["resolved", "closed", "cancelled", "awaiting_approval"].includes(d.state);
  if (!agent && !provider) return null;
  return (
    <section className="card" style={{ padding: 14 }}>
      <h3 className="sd-h3">IT provider</h3>
      {d.provider ? (
        <dl className="dl sd-dl">
          <dt>Provider</dt><dd>{d.provider.name}</dd>
          <dt>Handed over</dt><dd>{d.provider.at ? when(d.provider.at) : "–"}</dd>
          <dt>Their SLA</dt><dd><ClockChip c={d.provider.clock} at={d.provider.due} label="Provider resolve" /></dd>
          <dt>Their ref</dt><dd>
            <form className="btn-row" onSubmit={(e) => { e.preventDefault(); act(() => sd.update(d.number, { providerRef: ref }), "Saved their reference"); }}>
              <input className="input cell" style={{ width: 120 }} aria-label="Provider's ticket number" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Their ticket no." />
              <button className="btn" style={{ height: 28 }}>Save</button>
            </form></dd>
        </dl>
      ) : <p className="muted" style={{ marginTop: 0 }}>Not handed to the provider.</p>}
      {agent && open && d.state !== "with_provider" && providers.length > 0 && (
        <div style={{ marginTop: 10 }}>
          {providers.length > 1 && <select className="select" aria-label="Which provider" value={qid} onChange={(e) => setQid(e.target.value)} style={{ marginBottom: 6 }}>{providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>}
          <textarea className="textarea" rows={2} aria-label="Note for the provider" placeholder="What you've tried, what they need to know" value={note} onChange={(e) => setNote(e.target.value)} />
          <button className="btn primary" style={{ marginTop: 6 }} onClick={async () => { if (await act(() => sd.toProvider(d.number, { queueId: qid, note }), "Sent to the provider")) setNote(""); }}>Send to provider</button>
        </div>
      )}
      {d.state === "with_provider" && (
        <button className="btn" style={{ marginTop: 10 }} onClick={() => act(() => sd.giveBack(d.number), "Back with the desk")}>{provider ? "Hand back to the company" : "Take it back"}</button>
      )}
    </section>
  );
}

function LinksBox({ d, agent, act }: { d: TicketDetail; agent: boolean; act: (fn: () => Promise<unknown>, done?: string) => Promise<boolean> }) {
  const [entity, setEntity] = useState(ERP_ENTITIES[0]?.value ?? "item");
  const [key, setKey] = useState("");
  return (
    <section className="card" style={{ padding: 14 }}>
      <h3 className="sd-h3">Business Central records</h3>
      {d.links.length ? <div className="btn-row">{d.links.map((l) => <span key={l.id} className="chip-bc"><span className="k">{l.entity.replace(/_/g, " ")}</span><span className="v">{l.displayKey}</span></span>)}</div> : <p className="muted" style={{ marginTop: 0 }}>None linked.</p>}
      {agent && (
        <form className="btn-row" style={{ marginTop: 8 }} onSubmit={async (e) => { e.preventDefault(); if (await act(() => sd.link(d.number, entity, key))) setKey(""); }}>
          <select className="select cell" aria-label="Record kind" style={{ width: 130 }} value={entity} onChange={(e) => setEntity(e.target.value)}>
            {[...ERP_ENTITIES.map((x) => ({ value: x.value, label: x.label })), { value: "sales_order", label: "Sales order" }, { value: "purchase_order", label: "Purchase order" }, { value: "user", label: "BC user" }, { value: "job_queue_entry", label: "Job queue entry" }]
              .filter((x, i, a) => a.findIndex((y) => y.value === x.value) === i).map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
          </select>
          <input className="input cell" style={{ width: 120 }} aria-label="Record number" placeholder="Number" value={key} onChange={(e) => setKey(e.target.value)} />
          <button className="btn" style={{ height: 28 }} disabled={!key.trim()}>Link</button>
        </form>
      )}
    </section>
  );
}
